import React, { useMemo } from 'react'

import styled from '@emotion/styled'
import parse, { type DOMNode, domToReact, type HTMLReactParserOptions } from 'html-react-parser'

import { spacing, StructuredContent } from '@cash-design-system/react'
import { StyledStructuredContentForArticle } from '@cash-web/support/ui-components'

const StyledStructuredContent = styled(StructuredContent)`
  ${StyledStructuredContentForArticle}

  /* Center images at the top level of the article, ie. not nested within paragraphs */
  & > div > img {
    margin: 0 auto;
    display: block;
    margin-bottom: ${spacing.s};
  }
`

const MultiImageContainer = styled.div`
  display: flex;
  justify-content: start;
  gap: ${spacing.s};
  flex-wrap: wrap;
`

// HTML parser options for handling multiple images
const htmlParserOptions: HTMLReactParserOptions = {
  replace: node => {
    if (
      node.type === 'tag' &&
      (node.name === 'p' || node.name === 'div') // target wrappers
    ) {
      const children = node.children || []

      // Check if all children are img tags and there are children
      const allChildrenAreImages =
        children.length > 0 && children.every(child => child.type === 'tag' && child.name === 'img')

      // If all children are images, wrap in centered div
      if (allChildrenAreImages) {
        return <MultiImageContainer>{domToReact(children as DOMNode[], htmlParserOptions)}</MultiImageContainer>
      }
    }
    return undefined
  },
}

interface ParsedArticleContentProps {
  content: string
}

const ParsedArticleContent: React.FC<ParsedArticleContentProps> = ({ content }) => {
  const parsedContent = useMemo(() => {
    return parse(content, htmlParserOptions)
  }, [content])

  return <StyledStructuredContent>{parsedContent}</StyledStructuredContent>
}

export default ParsedArticleContent
